import * as go from 'gojs/release/go-module';

import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { ModelDataHighlight } from 'src/containers/ProjectDiagram/constants/highlight';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import {
  Diagram,
  ModelConceptRelationshipLink,
} from 'src/containers/ProjectDiagram/types/diagram';
import { ModelDataHighlighted } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isLink } from 'src/containers/ProjectDiagram/utils/graphObject';
import { disconnectModelConceptRelationshipForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/disconnectModelConceptRelationshipForClass';
import { setDiagramHighlight } from 'src/containers/ProjectDiagram/utils/setDiagramHighlight';
import { setModelHighlight } from 'src/containers/ProjectDiagram/utils/setModelHighlight';

const endMouseEnter =
  (isHead: boolean): NonNullable<go.Shape['mouseEnter']> =>
  (e, targetObject: go.GraphObject) => {
    e.handled = true;
    const graphObject = targetObject.part || targetObject;
    if (
      isLink<ModelConceptRelationshipLink>(
        graphObject,
        LinkCategory.MODEL_CONCEPT_RELATIONSHIP
      )
    ) {
      const highlights = new go.Set<go.Part>([]);
      let modelHighlighted: ModelDataHighlighted | null = null;

      if (isHead ? graphObject.toNode : graphObject.fromNode) {
        e.diagram.links
          .filter(
            (link) =>
              isLink<ModelConceptRelationshipLink>(
                link,
                LinkCategory.MODEL_CONCEPT_RELATIONSHIP
              ) &&
              link.data.value === graphObject.data.value &&
              (isHead
                ? link.data.to === graphObject.data.to
                : link.data.from === graphObject.data.from)
          )
          .each((link) => {
            highlights.add(link);
            highlights.addAll(link.labelNodes);
          });

        modelHighlighted = {
          type: isHead
            ? ModelDataHighlight.LINK_HEAD
            : ModelDataHighlight.LINK_TAIL,
          key: graphObject.data.key,
        };
      } else {
        highlights.add(graphObject);
        highlights.addAll(graphObject.labelNodes);
      }

      commitDiagramWhileSkippingUndoManager(
        e.diagram as Diagram,
        (_, diagram) => setDiagramHighlight(highlights)(diagram),
        DiagramTransaction.SET_DIAGRAM_HIGHLIGHT
      );
      commitDiagramWhileSkippingUndoManager(
        e.diagram as Diagram,
        setModelHighlight(modelHighlighted),
        DiagramTransaction.SET_MODEL_HIGHLIGHT
      );
    }
  };

export const headMouseEnter = endMouseEnter(true);
export const tailMouseEnter = endMouseEnter(false);

const endMouseLeave =
  (_isHead: boolean): NonNullable<go.Shape['mouseLeave']> =>
  (e, targetObject: go.GraphObject, nextObject: go.GraphObject | null) => {
    e.handled = true;
    const graphObject = targetObject.part || targetObject;
    if (
      isLink<ModelConceptRelationshipLink>(
        graphObject,
        LinkCategory.MODEL_CONCEPT_RELATIONSHIP
      )
    ) {
      const nextGraphObject = nextObject?.part || nextObject;
      const highlights = new go.Set<go.Part>([]);
      if (nextGraphObject === graphObject) {
        highlights.add(graphObject);
        highlights.addAll(graphObject.labelNodes);
      }
      commitDiagramWhileSkippingUndoManager(
        e.diagram as Diagram,
        (_, diagram) => setDiagramHighlight(highlights)(diagram),
        DiagramTransaction.SET_DIAGRAM_HIGHLIGHT
      );
      commitDiagramWhileSkippingUndoManager(
        e.diagram as Diagram,
        setModelHighlight(null),
        DiagramTransaction.SET_MODEL_HIGHLIGHT
      );
    }
  };

export const headMouseLeave = endMouseLeave(true);
export const tailMouseLeave = endMouseLeave(false);

const unlinkClick =
  (isHead: boolean): NonNullable<go.Shape['click']> =>
  (e, targetObject: go.GraphObject) => {
    e.handled = true;
    const graphObject = (targetObject.part ||
      targetObject) as ModelConceptRelationshipLink;
    const linkedNode = isHead ? graphObject.toNode : graphObject.fromNode;

    if (
      !isLink<ModelConceptRelationshipLink>(
        graphObject,
        LinkCategory.MODEL_CONCEPT_RELATIONSHIP
      ) ||
      !linkedNode
    ) {
      return;
    }

    const diagram = e.diagram as Diagram;
    const { highlighted: modelHighlighted } = diagram.model.modelData;
    // `isTargetModelHighlighted` should always be true here so long as this
    // method is always called as a click handler for a link. Just an additional
    // safety check.
    const isTargetModelHighlighted = Boolean(
      modelHighlighted && modelHighlighted.key === graphObject.data.key
    );

    if (!isTargetModelHighlighted) {
      return;
    }

    // Re-layout only the linked node, the link, and its label
    const layoutPartial = new Set<go.Key>([
      linkedNode.data.key,
      graphObject.data.key,
    ]);
    graphObject.labelNodes.each((labelNode) =>
      layoutPartial.add(labelNode.data.key)
    );

    commitDiagramModel(
      e.diagram as Diagram,
      disconnectModelConceptRelationshipForClass({
        classIri: linkedNode.data.value,
        direction: isHead
          ? RelationshipDirection.INCOMING
          : RelationshipDirection.OUTGOING,
        iri: graphObject.data.value,
      }),
      {
        layout: DiagramModelCommitLayout.PARTIAL,
        layoutPartial,
      }
    );
  };

export const headUnlinkClick = unlinkClick(true);
export const tailUnlinkClick = unlinkClick(false);
